import { Color } from 'three';
import { ColorMap } from '../types';

export const VIRIDIS_COLORS = [
  '#440154',
  '#482777',
  '#3f4a8a',
  '#31678e',
  '#26838f',
  '#1f9d8a',
  '#6cce5a',
  '#b6de2b',
  '#fee825',
];

export const PLASMA_COLORS = ['#0d0887', '#6a00a8', '#b12a90', '#e16462', '#fca636', '#f0f921'];

export const INFERNO_COLORS = ['#000004', '#420a68', '#932667', '#dd513a', '#fca50a', '#fcffa4'];

export const TURBO_COLORS = [
  '#30123b',
  '#4145ab',
  '#4675ed',
  '#39a2fc',
  '#1bcfd4',
  '#24eca6',
  '#61fc6c',
  '#a4fc3b',
  '#d1ed00',
  '#f1ca3a',
  '#fe9b2d',
  '#e75263',
  '#c42e60',
  '#a01c7a',
  '#781c6d',
];

export const COLOR_MAPS: Record<string, ColorMap> = {
  viridis: {
    name: 'Viridis',
    colors: VIRIDIS_COLORS,
    stops: [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875, 1.0],
  },
  plasma: {
    name: 'Plasma',
    colors: PLASMA_COLORS,
    stops: [0, 0.2, 0.4, 0.6, 0.8, 1.0],
  },
  inferno: {
    name: 'Inferno',
    colors: INFERNO_COLORS,
    stops: [0, 0.2, 0.4, 0.6, 0.8, 1.0],
  },
  turbo: {
    name: 'Turbo',
    colors: TURBO_COLORS,
    stops: [0, 0.07, 0.14, 0.21, 0.28, 0.35, 0.42, 0.49, 0.56, 0.63, 0.7, 0.77, 0.84, 0.91, 1.0],
  },
  cool: {
    name: 'Cool',
    colors: ['#00ffff', '#ff00ff'],
    stops: [0, 1.0],
  },
  hot: {
    name: 'Hot',
    colors: ['#000000', '#ff0000', '#ffff00', '#ffffff'],
    stops: [0, 0.33, 0.66, 1.0],
  },
};

export const interpolateColor = (color1: string, color2: string, factor: number): string => {
  // Convert hex to RGB
  const hex2rgb = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };

  // Convert RGB to hex
  const rgb2hex = (r: number, g: number, b: number) => {
    return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
  };

  const [r1, g1, b1] = hex2rgb(color1);
  const [r2, g2, b2] = hex2rgb(color2);

  const r = r1 + (r2 - r1) * factor;
  const g = g1 + (g2 - g1) * factor;
  const b = b1 + (b2 - b1) * factor;

  return rgb2hex(r, g, b);
};

export const getColorFromMap = (value: number, min: number, max: number, mapName: string = 'viridis'): string => {
  if (min >= max) return COLOR_MAPS[mapName]?.colors[0] || '#000000';

  const colorMap = COLOR_MAPS[mapName] || COLOR_MAPS.viridis;
  const normalizedValue = Math.max(0, Math.min(1, (value - min) / (max - min)));

  // Find the appropriate color segment
  for (let i = 0; i < colorMap.stops.length - 1; i++) {
    const stop1 = colorMap.stops[i];
    const stop2 = colorMap.stops[i + 1];

    if (normalizedValue >= stop1 && normalizedValue <= stop2) {
      const segmentFactor = (normalizedValue - stop1) / (stop2 - stop1);
      return interpolateColor(colorMap.colors[i], colorMap.colors[i + 1], segmentFactor);
    }
  }

  // Fallback to last color
  return colorMap.colors[colorMap.colors.length - 1];
};

export const generateHeatmapTexture = (
  width: number,
  height: number,
  dataPoints: Array<{ x: number; y: number; value: number }>,
  minValue: number,
  maxValue: number,
  colorMapName: string = 'viridis',
  radius: number = 20,
): ImageData => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let totalWeight = 0;
      let weightedValue = 0;

      // Calculate influence from all data points
      dataPoints.forEach((point) => {
        const dx = x - point.x;
        const dy = y - point.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance <= radius) {
          const weight = Math.exp(-(distance * distance) / (2 * radius * radius));
          totalWeight += weight;
          weightedValue += point.value * weight;
        }
      });

      const pixelIndex = (y * width + x) * 4;

      if (totalWeight > 0) {
        const interpolatedValue = weightedValue / totalWeight;
        const color = getColorFromMap(interpolatedValue, minValue, maxValue, colorMapName);

        // Convert hex to RGB
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);

        data[pixelIndex] = r;
        data[pixelIndex + 1] = g;
        data[pixelIndex + 2] = b;
        data[pixelIndex + 3] = Math.min(255, totalWeight * 255); // Alpha based on weight
      } else {
        // Transparent pixel
        data[pixelIndex] = 0;
        data[pixelIndex + 1] = 0;
        data[pixelIndex + 2] = 0;
        data[pixelIndex + 3] = 0;
      }
    }
  }

  return imageData;
};

export const getContrastColor = (backgroundColor: string): string => {
  // Convert hex to RGB
  const r = parseInt(backgroundColor.slice(1, 3), 16);
  const g = parseInt(backgroundColor.slice(3, 5), 16);
  const b = parseInt(backgroundColor.slice(5, 7), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black or white based on luminance
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

export const hexToRgb = (hex: string): [number, number, number] => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return [r, g, b];
};

export const rgbToHex = (r: number, g: number, b: number): string => {
  const rHex = Math.round(r * 255)
    .toString(16)
    .padStart(2, '0');
  const gHex = Math.round(g * 255)
    .toString(16)
    .padStart(2, '0');
  const bHex = Math.round(b * 255)
    .toString(16)
    .padStart(2, '0');
  return `#${rHex}${gHex}${bHex}`;
};

// Alias for getColorFromMap with sensor type support
export const getColorFromValue = (
  value: number,
  minValue: number,
  maxValue: number,
  sensorType: string = 'temperature',
): Color => {
  // Map sensor types to appropriate color maps
  const sensorColorMaps: Record<string, string> = {
    temperature: 'inferno',
    humidity: 'viridis',
    pressure: 'plasma',
    motion: 'cool',
    light: 'hot',
    'air-quality': 'turbo',
  };

  const colorMapName = sensorColorMaps[sensorType] || 'viridis';
  const hexColor = getColorFromMap(value, minValue, maxValue, colorMapName);

  // Return Three.js Color object
  return new Color(hexColor);
};
